/**
 * A quotation's edit history, read from Frappe's `Version` doctype.
 *
 * `/api/resource` rather than a `quoflow.*` method: there is no method for this, and `Version` is
 * a stock Frappe doctype every site has. Part of the residual resource surface documented in
 * `frappe.ts`'s header — `fields` is mandatory there, so every column is named.
 *
 * Read-only and side-effect free. See `lib/quotationHistory.ts` for what the rows contain and why
 * the parsing is deliberately conservative.
 */

import { useQuery } from '@tanstack/react-query'
import { getResource } from './frappe'
import { toHistory, type WireVersion } from '../quotationHistory'
import { qk } from './keys'

/**
 * Enough rows to cover a quotation's whole life. Measured 2026-08-09: the most-edited quotation on
 * the bench had 8. A cap is still needed — `Version` grows without bound across the site — and
 * `limit` is explicit so a silent server default can never decide how much history is "all" of it.
 */
const HISTORY_LIMIT = 50

export function useQuotationHistory(quotationName: string | null | undefined) {
  return useQuery({
    queryKey: qk.quotationHistory(quotationName ?? ''),
    queryFn: async () => {
      const page = await getResource<WireVersion>('Version', {
        filters: [
          ['ref_doctype', '=', 'Quotation'],
          ['docname', '=', quotationName ?? ''],
        ],
        fields: ['name', 'creation', 'owner', 'data'],
        orderBy: 'creation desc',
        limit: HISTORY_LIMIT,
      })
      return toHistory(page.data)
    },
    enabled: !!quotationName,
  })
}
